import { useGetEnrollmentJointEmploymentData, useSubmitEnrollmentJointEmploymentData } from '@egw/api'
import { EmploymentStatus } from '@egw/api/src/models/enrollment/enrollment'
import { Alert, AlertType, Button, GenericError, ProgressBar, RadioGroup, Tag } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { type FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { sendEvent } from '@egw/events'
import { UserIcon } from '@heroicons/react/16/solid'
import { PageHeader } from '../../../components'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { ROUTES as JOINT_ROUTES } from '../../../constants/routes/routes-joint'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import {
  EMPLOYMENT_STATUS,
  employmentStatusItems,
  occupationItems,
} from '../../../constants/confirm-personal-info/confirm-personal-info'

const log = new Logger('Enrollment - Joint - Employment')

interface FormData {
  employmentStatus: string
  occupation: string
}

export const EmploymentPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentJointEmploymentData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointEmploymentData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint employment data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint employment form', submitError)
    }
  }, [submitError])

  const progress = data?.activePerson.progress
  const profileName = data?.activePerson.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''
  const applicationId = data && !data.isError ? data.activePerson?.applicationId : ''

  // Protect route
  useValidateRoute(data?.activePerson.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - Employment')

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      employmentStatus: '',
      occupation: '',
    },
  })

  const _employmentStatus = data?.activePerson.employmentStatus ?? ''
  const _occupation = data?.activePerson.occupation ?? ''

  // Update form values when data is loaded
  useEffect(() => {
    if (data?.activePerson) {
      reset({
        employmentStatus: _employmentStatus,
        occupation: _occupation,
      })
    }
  }, [data, reset, _employmentStatus, _occupation])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const occupation = formData.occupation ? formData.occupation : undefined

        const response = await mutate({
          activePerson: {
            employmentStatus: formData.employmentStatus,
            occupation,
          },
        })

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        sendEvent('bankmulti_enroll_employment_submit', {
          applicationId,
          employmentStatus: formData.employmentStatus,
        })

        // If path exists and is different
        if (response?.activePerson.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          sendEvent('user_enroll_employment_verified', {
            applicationId,
            employmentStatus: formData.employmentStatus,
          })
          void navigate(response.activePerson.path)
        } else if (!response?.activePerson.path) {
          // If no path from BE, redirect to error
          log.error('No path returned from backend, redirecting to error page')
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting employment form', e)
      }
    }
  })

  const isEmployedOrIndependent =
    (watch(EMPLOYMENT_STATUS) as EmploymentStatus) === EmploymentStatus.Employed ||
    (watch(EMPLOYMENT_STATUS) as EmploymentStatus) === EmploymentStatus.Independent

  return (
    <EnrollmentJointLayout title={t('joint.employment.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t('joint.employment.title')}
            // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.VERIFY_IDENTITY.path}`}
          />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-xl" />

          <p className="typo-primary-base-semibold mb-sm">{t('joint.employment.employment_status_title')}</p>
          <Controller
            name="employmentStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref: _ref, ...restField } }) => (
              <RadioGroup
                {...restField}
                onChange={(e) => {
                  restField.onChange(e)
                  if (
                    [EmploymentStatus.Unemployed, EmploymentStatus.Retired, EmploymentStatus.Student].includes(
                      e as EmploymentStatus,
                    )
                  ) {
                    setValue('occupation', '')
                  }
                }}
                options={employmentStatusItems.map((item) => ({
                  value: item.value,
                  label: t(`joint.employment.employment_status.${item.value}`),
                }))}
                name="employmentStatus"
                className="!py-xs mb-xl typo-primary-base"
                testId="employment-status-radio"
              />
            )}
          />

          {isEmployedOrIndependent ? (
            <>
              <p className="typo-primary-base-semibold mb-sm">{t('joint.employment.occupation_title')}</p>
              <Controller
                name="occupation"
                control={control}
                rules={{
                  required: isEmployedOrIndependent,
                }}
                render={({ field: { ref: _ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    onChange={(e) => {
                      restField.onChange(e)
                    }}
                    options={occupationItems.map((item) => ({
                      value: item.value,
                      label: t(`joint.employment.occupation.${item.value}`),
                    }))}
                    name="occupation"
                    className="py-xs mb-xl"
                    testId="occupation-radio"
                  />
                )}
              />
            </>
          ) : null}

          <div className="flex-1 md:hidden" />

          {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {t('common.continue')}
          </Button>
        </form>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
